import range from 'lodash/range';
import React, { useMemo } from 'react';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import { FormGroup } from '@mui/material';
import {
  LEADERBOARD_ABOVE_MAX_LIMIT,
  LEADERBOARD_TOP_MAX_LIMIT,
} from '@loyalty/shared-common/leaderboard-api-client';
import { LeaderboardTableComponentAttributes } from '@loyalty/editor-components';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SelectField } from '../../../../shared/forms/fields/SelectField';
import { SwitchField } from '../../../../shared/forms/fields/SwitchField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import { useCurrencies } from '../../../../currency/CurrenciesContext';

const nameFormatOptions = [
  { id: 'fullname', name: 'John Doe' },
  { id: 'firstNameInitialLastname', name: 'John D.' },
  { id: 'onlyFirstname', name: 'John' },
  { id: 'fullInitials', name: 'JD.' },
];

type FormValue = TraitFormValue<LeaderboardTableComponentAttributes, void>;

export function LeaderboardTableWidgetTraits() {
  const { t, i18n } = useTranslation();
  const { currencies } = useCurrencies();

  const { watch, control } = useFormContext<FormValue>();

  const currencyOptions = useMemo(
    () =>
      currencies
        .filter(value => !value.deletedAt)
        .map((currency: CurrencyAttributes) => ({
          id: currency.id,
          name: getLocalizedProperty(currency, 'name', i18n.language) || 'No name',
        })),

    [currencies, i18n.language, t],
  );

  const showProfileName = watch('attributes.showProfileName');

  return (
    <FormGroup>
      <Trait>
        <SwitchField<FormValue>
          name="attributes.showProfilePicture"
          label={t('editor.leaderboard.traits.showProfilePicture')}
        />
      </Trait>

      <Trait>
        <SwitchField<FormValue>
          name="attributes.showProfileName"
          label={t('editor.leaderboard.traits.showProfileName')}
        />
      </Trait>

      {showProfileName && (
        <Trait>
          <SelectField
            control={control}
            name="attributes.profileNameFormat"
            label={t('editor.leaderboard.traits.profileNameFormat')}
            options={nameFormatOptions}
          />
        </Trait>
      )}
      <Trait>
        <SelectField
          control={control}
          name="attributes.currencyId"
          label={t('editor.leaderboard.traits.currency')}
          options={currencyOptions}
        />
      </Trait>
      <Trait>
        <SelectField
          control={control}
          name="attributes.topLimit"
          label={t('editor.leaderboard.traits.top-limit')}
          options={range(0, LEADERBOARD_TOP_MAX_LIMIT + 1).map(value => ({
            id: `${value}`,
            name: value.toString(),
          }))}
        />
      </Trait>
      <Trait>
        <SelectField
          control={control}
          name="attributes.aboveLimit"
          label={t('editor.leaderboard.traits.above-limit')}
          options={range(0, LEADERBOARD_ABOVE_MAX_LIMIT + 1).map(value => ({
            id: `${value}`,
            name: value.toString(),
          }))}
        />
      </Trait>
      <Trait>
        <SelectField
          control={control}
          name="attributes.belowLimit"
          label={t('editor.leaderboard.traits.below-limit')}
          options={range(0, LEADERBOARD_ABOVE_MAX_LIMIT + 1).map(value => ({
            id: `${value}`,
            name: value.toString(),
          }))}
        />
      </Trait>
    </FormGroup>
  );
}
